import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../features/auth/AuthProvider.js";
import * as inventoryApi from "../services/inventory-api.js";
import * as ordersApi from "../services/orders-api.js";
import * as podApi from "../services/pod-api.js";
import * as tenantsApi from "../services/tenants-api.js";
import { uploadMedia } from "../services/media-api.js";
import { ApiClientError } from "../services/api-client.js";
import type { OrderDetailResponse, OrderListItem, OrderStatus, Product, TenantSummary } from "../types/api.js";
import { orderPodComplianceLabel } from "../lib/pod-compliance-copy.js";

function isPlatformRole(role: string): boolean {
  return role === "superadmin" || role === "platform-support";
}

const STATUS_OPTIONS: OrderStatus[] = [
  "draft",
  "submitted",
  "confirmed",
  "cancelled",
  "packed",
  "shipped",
  "delivered",
];

export function OrdersPage(): JSX.Element {
  const { token, user, setFlash } = useAuth();
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<OrderDetailResponse | null>(null);
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [detailLoading, setDetailLoading] = useState(false);
  const [podLoading, setPodLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [podError, setPodError] = useState<string | null>(null);
  const [pod, setPod] = useState<import("../types/api.js").ProofOfDelivery | null>(null);
  const [podAttempts, setPodAttempts] = useState<import("../types/api.js").ProofOfDelivery[] | null>(null);
  const [createMsg, setCreateMsg] = useState<string | null>(null);

  const [podSubmitting, setPodSubmitting] = useState(false);
  const [podSubmitMsg, setPodSubmitMsg] = useState<string | null>(null);
  const [podForm, setPodForm] = useState({
    isAccepted: true,
    confirmationType: "manual" as "verbal" | "manual" | "system",
    receiverName: "",
    deliveryReference: "",
    notes: "",
    deliveryOutcome: "" as "" | "accepted" | "refused" | "partial" | "other",
    receiverDocumentId: "",
    receiverRole: "",
    acceptanceReason: "",
    evidenceText: "",
    evidencePhotoUrl: "",
    evidenceSignatureUrl: "",
    capturedLat: "",
    capturedLng: "",
    capturedAccuracyM: "",
    capturedLocationProvider: "",
    capturedLocationAt: "",
    captureNotes: "",
    substitutionReason: "",
  });
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "">("");
  const [orderNumberFilter, setOrderNumberFilter] = useState("");

  /* ── Picking state ── */
  const [pickLine, setPickLine] = useState<{ lineId: string; lineLabel: string } | null>(null);
  const [pickQty, setPickQty] = useState(1);
  const [pickLoading, setPickLoading] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);

  /* ── Pack state ── */
  const [packLoading, setPackLoading] = useState(false);
  const [packError, setPackError] = useState<string | null>(null);

  /* ── Ship state ── */
  const [showShipForm, setShowShipForm] = useState(false);
  const [shipLoading, setShipLoading] = useState(false);
  const [shipError, setShipError] = useState<string | null>(null);
  const [shipForm, setShipForm] = useState({
    carrierName: "",
    carrierId: "",
    carrierCode: "",
    serviceLevel: "",
    externalReference: "",
    trackingReference: "",
    dispatchReference: "",
    shippingNotes: "",
    shippedAt: "",
  });

  /* ── Deliver state ── */
  const [showDeliverForm, setShowDeliverForm] = useState(false);
  const [deliverLoading, setDeliverLoading] = useState(false);
  const [deliverError, setDeliverError] = useState<string | null>(null);
  const [deliverForm, setDeliverForm] = useState({
    deliveredAt: "",
    deliveryReference: "",
    receiverName: "",
    deliveryNotes: "",
    statusReason: "",
  });

  /* ── Media upload (POD form) ── */
  const [photoUploading, setPhotoUploading] = useState(false);
  const [sigUploading, setSigUploading] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);

  const [formTenantId, setFormTenantId] = useState("");
  const [formOrderNumber, setFormOrderNumber] = useState("");
  const [formCustomerRef, setFormCustomerRef] = useState("");
  const [formLines, setFormLines] = useState([{ productId: "", quantity: 1 }]);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [statusPatching, setStatusPatching] = useState(false);

  const tenantQuery = useMemo(() => {
    if (user?.tenantId) return { tenantId: user.tenantId };
    return {};
  }, [user?.tenantId]);

  const needsTenantPicker = Boolean(user && isPlatformRole(user.role) && !user.tenantId);

  const effectiveFormTenantId = needsTenantPicker ? formTenantId : user?.tenantId ?? "";

  const loadOrders = useCallback(async () => {
    if (!token) return;
    setLoadState("loading");
    setErrorMsg(null);
    try {
      const params = {
        ...tenantQuery,
        status: statusFilter || undefined,
        orderNumber: orderNumberFilter.trim() || undefined,
      };
      const { orders: list } = await ordersApi.fetchOrders(token, params);
      setOrders(list);
      setLoadState("ok");
      setSelectedId((sid) => {
        if (sid && !list.some((o) => o.id === sid)) {
          setDetail(null);
          return null;
        }
        return sid;
      });
    } catch (e) {
      setLoadState("error");
      setErrorMsg(e instanceof ApiClientError ? e.message : "No se pudieron cargar los pedidos.");
    }
  }, [token, tenantQuery, statusFilter, orderNumberFilter]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!token || !needsTenantPicker) return;
    let cancelled = false;
    void tenantsApi
      .fetchTenants(token)
      .then((r) => {
        if (cancelled) return;
        setTenants(r.tenants);
        setFormTenantId((prev) => prev || (r.tenants[0]?.id ?? ""));
      })
      .catch(() => {
        if (!cancelled) setTenants([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, needsTenantPicker]);

  useEffect(() => {
    if (!token) return;
    const tid = effectiveFormTenantId;
    if (!tid) {
      setProducts([]);
      return;
    }
    let cancelled = false;
    void inventoryApi
      .fetchProducts(token, { tenantId: tid })
      .then((r) => {
        if (!cancelled) setProducts(r.products.filter((p) => p.isActive));
      })
      .catch(() => {
        if (!cancelled) setProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, effectiveFormTenantId]);

  const loadDetail = useCallback(
    async (id: string) => {
      if (!token) return;
      setDetailLoading(true);
      try {
        const d = await ordersApi.fetchOrderDetail(token, id);
        setDetail(d);
        setPod(null);
        setPodAttempts(null);
        setPodError(null);
      } catch (e) {
        setDetail(null);
        setErrorMsg(e instanceof ApiClientError ? e.message : "No se pudo cargar el detalle.");
      } finally {
        setDetailLoading(false);
      }
    },
    [token],
  );

  const loadPod = useCallback(
    async (id: string) => {
      if (!token) return;
      setPodLoading(true);
      setPodError(null);
      try {
        const r = await podApi.fetchOrderPod(token, id);
        setPod(r.pod);
      } catch (e) {
        setPod(null);
        setPodError(e instanceof ApiClientError ? e.message : "No se pudo cargar el POD.");
      } finally {
        setPodLoading(false);
      }
    },
    [token],
  );

  const submitPod = useCallback(async (): Promise<void> => {
    if (!token || !detail) return;
    setPodSubmitting(true);
    setPodSubmitMsg(null);
    setPodError(null);
    try {
      const body = {
        isAccepted: podForm.isAccepted,
        confirmationType: podForm.confirmationType,
        receiverName: podForm.receiverName.trim() || null,
        deliveryReference: podForm.deliveryReference.trim() || null,
        notes: podForm.notes.trim() || null,
        deliveryOutcome: (podForm.deliveryOutcome || null) as any,
        receiverDocumentId: podForm.receiverDocumentId.trim() || null,
        receiverRole: podForm.receiverRole.trim() || null,
        acceptanceReason: podForm.acceptanceReason.trim() || null,
        evidenceText: podForm.evidenceText.trim() || null,
        evidencePhotoUrl: podForm.evidencePhotoUrl.trim() || null,
        evidenceSignatureUrl: podForm.evidenceSignatureUrl.trim() || null,
        capturedLat: podForm.capturedLat.trim() ? Number(podForm.capturedLat) : null,
        capturedLng: podForm.capturedLng.trim() ? Number(podForm.capturedLng) : null,
        capturedAccuracyM: podForm.capturedAccuracyM.trim() ? Number(podForm.capturedAccuracyM) : null,
        capturedLocationProvider: podForm.capturedLocationProvider.trim() || null,
        capturedLocationAt: podForm.capturedLocationAt.trim() || null,
        captureNotes: podForm.captureNotes.trim() || null,
        substitutionReason: podForm.substitutionReason.trim() || null,
      };
      await podApi.createOrderPod(token, detail.order.id, body);
      setPodSubmitMsg("POD registrado.");
      await loadOrders();
      await loadDetail(detail.order.id);
      await loadPod(detail.order.id);
    } catch (e) {
      setPodSubmitMsg(null);
      setPodError(e instanceof ApiClientError ? e.message : "No se pudo registrar el POD.");
    } finally {
      setPodSubmitting(false);
    }
  }, [token, detail, podForm, loadOrders, loadDetail, loadPod]);

  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const addFormLine = (): void => {
    setFormLines((prev) => [...prev, { productId: "", quantity: 1 }]);
  };

  const updateFormLine = (i: number, patch: Partial<{ productId: string; quantity: number }>): void => {
    setFormLines((prev) => prev.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  };

  const removeFormLine = (i: number): void => {
    setFormLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, j) => j !== i)));
  };

  const submitCreate = async (): Promise<void> => {
    if (!token) return;
    setCreateMsg(null);
    const tenantId = needsTenantPicker ? formTenantId : undefined;
    if (needsTenantPicker && !tenantId) {
      setCreateMsg("Seleccione un tenant para el pedido.");
      return;
    }
    const lines = formLines
      .filter((l) => l.productId)
      .map((l) => ({ productId: l.productId, quantity: l.quantity }));
    if (lines.length === 0) {
      setCreateMsg("Añada al menos una línea con producto.");
      return;
    }
    if (lines.some((l) => l.quantity <= 0)) {
      setCreateMsg("Las cantidades deben ser mayores que cero.");
      return;
    }
    setCreateSubmitting(true);
    try {
      await ordersApi.createOrder(token, {
        tenantId,
        orderNumber: formOrderNumber.trim() || undefined,
        customerReference: formCustomerRef.trim() || null,
        status: "draft",
        lines,
      });
      setFormOrderNumber("");
      setFormCustomerRef("");
      setFormLines([{ productId: "", quantity: 1 }]);
      setCreateMsg("Pedido creado correctamente.");
      setFlash("Pedido creado.");
      void loadOrders();
    } catch (e) {
      setCreateMsg(e instanceof ApiClientError ? e.message : "Error al crear el pedido.");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const applyStatusPatch = async (next: OrderStatus): Promise<void> => {
    if (!token || !selectedId || !detail) return;
    setStatusPatching(true);
    setErrorMsg(null);
    try {
      await ordersApi.patchOrderStatus(token, selectedId, next);
      await loadDetail(selectedId);
      void loadOrders();
    } catch (e) {
      setErrorMsg(e instanceof ApiClientError ? e.message : "No se pudo actualizar el estado.");
    } finally {
      setStatusPatching(false);
    }
  };

  const nextStatusOptions = useMemo((): OrderStatus[] => {
    const s = detail?.order.status;
    if (!s) return [];
    if (s === "draft") return ["submitted", "cancelled"];
    if (s === "submitted") return ["confirmed", "cancelled"];
    if (s === "confirmed") return ["cancelled"];
    return [];
  }, [detail?.order.status]);

  /* ── Handlers operativos ── */

  const submitPick = useCallback(async () => {
    if (!token || !detail || !pickLine) return;
    setPickLoading(true);
    setPickError(null);
    try {
      await ordersApi.pickOrderLine(token, detail.order.id, pickLine.lineId, pickQty);
      setPickLine(null);
      await loadDetail(detail.order.id);
      void loadOrders();
    } catch (e) {
      setPickError(e instanceof ApiClientError ? e.message : "Error al registrar picking.");
    } finally {
      setPickLoading(false);
    }
  }, [token, detail, pickLine, pickQty, loadDetail, loadOrders]);

  const submitPack = useCallback(async () => {
    if (!token || !detail) return;
    setPackLoading(true);
    setPackError(null);
    try {
      await ordersApi.packOrder(token, detail.order.id);
      await loadDetail(detail.order.id);
      void loadOrders();
    } catch (e) {
      setPackError(e instanceof ApiClientError ? e.message : "Error al empaquetar pedido.");
    } finally {
      setPackLoading(false);
    }
  }, [token, detail, loadDetail, loadOrders]);

  const submitShip = useCallback(async () => {
    if (!token || !detail) return;
    setShipLoading(true);
    setShipError(null);
    try {
      await ordersApi.shipOrder(token, detail.order.id, {
        carrierName: shipForm.carrierName.trim() || null,
        carrierId: shipForm.carrierId.trim() || null,
        carrierCode: shipForm.carrierCode.trim() || null,
        serviceLevel: shipForm.serviceLevel.trim() || null,
        externalReference: shipForm.externalReference.trim() || null,
        trackingReference: shipForm.trackingReference.trim() || null,
        dispatchReference: shipForm.dispatchReference.trim() || null,
        shippingNotes: shipForm.shippingNotes.trim() || null,
        shippedAt: shipForm.shippedAt.trim() || null,
      });
      setShowShipForm(false);
      await loadDetail(detail.order.id);
      void loadOrders();
    } catch (e) {
      setShipError(e instanceof ApiClientError ? e.message : "Error al expedir pedido.");
    } finally {
      setShipLoading(false);
    }
  }, [token, detail, shipForm, loadDetail, loadOrders]);

  const submitDeliver = useCallback(async () => {
    if (!token || !detail) return;
    setDeliverLoading(true);
    setDeliverError(null);
    try {
      await ordersApi.deliverOrder(token, detail.order.id, {
        deliveredAt: deliverForm.deliveredAt.trim() || null,
        deliveryReference: deliverForm.deliveryReference.trim() || null,
        receiverName: deliverForm.receiverName.trim() || null,
        deliveryNotes: deliverForm.deliveryNotes.trim() || null,
        statusReason: deliverForm.statusReason.trim() || null,
      });
      setShowDeliverForm(false);
      await loadDetail(detail.order.id);
      void loadOrders();
    } catch (e) {
      setDeliverError(e instanceof ApiClientError ? e.message : "Error al registrar entrega.");
    } finally {
      setDeliverLoading(false);
    }
  }, [token, detail, deliverForm, loadDetail, loadOrders]);

  const handlePhotoUpload = useCallback(
    async (file: File) => {
      if (!token) return;
      setPhotoUploading(true);
      setPhotoUploadError(null);
      try {
        const { url } = await uploadMedia(token, file);
        setPodForm((f) => ({ ...f, evidencePhotoUrl: url }));
      } catch (e) {
        setPhotoUploadError(e instanceof Error ? e.message : "Error al subir foto.");
      } finally {
        setPhotoUploading(false);
      }
    },
    [token],
  );

  const handleSigUpload = useCallback(
    async (file: File) => {
      if (!token) return;
      setSigUploading(true);
      try {
        const { url } = await uploadMedia(token, file);
        setPodForm((f) => ({ ...f, evidenceSignatureUrl: url }));
      } catch (e) {
        setPhotoUploadError(e instanceof Error ? e.message : "Error al subir firma.");
      } finally {
        setSigUploading(false);
      }
    },
    [token],
  );

  return (
    <div className="inventory-page orders-page">
      <header className="page-header">
        <h1>Pedidos</h1>
        <p className="text-muted">
          Flujo operativo completo: <strong>draft → submitted → confirmed → picking → packed → shipped → delivered → POD</strong>.
          Picking por línea, expedición con carrier y nivel de servicio, entrega con receptor, POD con foto/firma (upload directo).
        </p>
        {user && needsTenantPicker ? (
          <p className="banner-inline" role="note">
            Sesión de plataforma sin tenant: puede ver todos los pedidos; para crear uno elija tenant y productos de ese
            catálogo.
          </p>
        ) : null}
      </header>

      <section className="card filters-card">
        <h2 className="section-title">Filtros</h2>
        <div className="filters-row">
          <label className="field-inline">
            <span>Estado</span>
            <select value={statusFilter} onChange={(e) => setStatusFilter((e.target.value || "") as OrderStatus | "")}>
              <option value="">Todos</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="field-inline">
            <span>Número de pedido contiene</span>
            <input value={orderNumberFilter} onChange={(e) => setOrderNumberFilter(e.target.value)} placeholder="ORD" />
          </label>
          <button type="button" className="btn btn-secondary" onClick={() => void loadOrders()} disabled={loadState === "loading"}>
            {loadState === "loading" ? "Cargando…" : "Actualizar listado"}
          </button>
        </div>
      </section>

      {loadState === "loading" && orders.length === 0 ? <p className="text-muted">Cargando pedidos…</p> : null}
      {loadState === "error" ? <p className="form-error">{errorMsg}</p> : null}

      {loadState === "ok" ? (
        <div className="orders-layout">
          <section className="card table-section">
            <h2 className="section-title">Listado</h2>
            {orders.length === 0 ? (
              <p className="text-muted empty-hint">No hay pedidos con los filtros actuales.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table orders-table">
                  <thead>
                    <tr>
                      <th>Número</th>
                      <th>Tenant</th>
                      <th>Ref. cliente</th>
                      <th>Estado</th>
                      <th>Reserva</th>
                      <th>POD / política</th>
                      <th className="num">Líneas</th>
                      <th>Creado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr
                        key={o.id}
                        className={selectedId === o.id ? "row-selected" : undefined}
                        onClick={() => setSelectedId(o.id)}
                        onKeyDown={(ev) => {
                          if (ev.key === "Enter" || ev.key === " ") {
                            ev.preventDefault();
                            setSelectedId(o.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <td>
                          <code>{o.orderNumber}</code>
                        </td>
                        <td>
                          <code>{o.tenantSlug}</code>
                        </td>
                        <td>{o.customerReference ?? "—"}</td>
                        <td>
                          <code>{o.status}</code>
                        </td>
                        <td>
                          {o.reservationSummary?.hasActiveReservations ? (
                            <span title={`${o.reservationSummary.activeReservationCount} filas activas`}>sí</span>
                          ) : (
                            <span className="text-muted">no</span>
                          )}
                        </td>
                        <td className="pod-policy-cell">
                          <span className="pod-policy-line" title={orderPodComplianceLabel(o)}>
                            {orderPodComplianceLabel(o)}
                          </span>
                        </td>
                        <td className="num">{o.lineCount}</td>
                        <td className="nowrap">{new Date(o.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card table-section order-detail-card">
            <h2 className="section-title">Detalle</h2>
            {!selectedId ? (
              <p className="text-muted empty-hint">Seleccione un pedido en la tabla.</p>
            ) : detailLoading ? (
              <p className="text-muted">Cargando detalle…</p>
            ) : detail ? (
              <>
                <dl className="order-meta">
                  <dt>Número</dt>
                  <dd>
                    <code>{detail.order.orderNumber}</code>
                  </dd>
                  <dt>Tenant</dt>
                  <dd>
                    <code>{detail.order.tenantSlug}</code> ({detail.order.tenantId.slice(0, 8)}…)
                  </dd>
                  <dt>Estado</dt>
                  <dd>
                    <code>{detail.order.status}</code>
                  </dd>
                  <dt>Ref. cliente</dt>
                  <dd>{detail.order.customerReference ?? "—"}</dd>
                  <dt>Actualizado</dt>
                  <dd className="nowrap">{new Date(detail.order.updatedAt).toLocaleString()}</dd>
                  <dt>Reserva activa</dt>
                  <dd>
                    {detail.order.reservationSummary?.hasActiveReservations ? (
                      <>
                        sí ({detail.order.reservationSummary.activeReservationCount} filas en{" "}
                        <code>order_reservations</code>)
                      </>
                    ) : (
                      "no"
                    )}
                  </dd>
                  <dt>POD / política (resumen)</dt>
                  <dd className="pod-detail-dd">{orderPodComplianceLabel(detail.order)}</dd>
                  <dt>Exige POD</dt>
                  <dd>{detail.order.requiresPodForDelivery === true ? "sí" : "no"}</dd>
                  <dt>Exige aceptación POD</dt>
                  <dd>{detail.order.requiresAcceptedPod === true ? "sí" : "no"}</dd>
                  <dt>Enforcement POD</dt>
                  <dd>{detail.order.podEnforcementActive === true ? "activo" : "no"}</dd>
                  <dt>Entrega bloqueada por política</dt>
                  <dd>
                    {detail.order.deliverBlockedByPodPolicy === true ? (
                      <span className="text-warn">sí</span>
                    ) : (
                      "no"
                    )}
                  </dd>
                  <dt>POD registrado</dt>
                  <dd>
                    {detail.order.hasPod === true ? (
                      <>
                        sí{" "}
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={podLoading}
                          onClick={() => void loadPod(detail.order.id)}
                        >
                          Ver POD
                        </button>
                      </>
                    ) : (
                      "no"
                    )}
                  </dd>
                  <dt>Conforme a política</dt>
                  <dd>{detail.order.isPodCompliant === true ? "sí" : "no"}</dd>
                </dl>
                {detail.order.hasPod !== true ? (
                  <section className="card">
                    <h3 className="subsection-title">Registrar POD (mínimo)</h3>
                    <p className="text-muted small">
                      Fase 7.5: registra POD desde el portal con campos base 6.7 y opcionales 7.1/7.5 (URLs y ubicación).
                      Sin binarios.
                    </p>
                    {podSubmitMsg ? <p className="text-muted">{podSubmitMsg}</p> : null}
                    {podError ? <p className="form-error">{podError}</p> : null}
                    <div className="form-grid">
                      <label className="field-inline field-block">
                        <span>Aceptado</span>
                        <select
                          value={podForm.isAccepted ? "true" : "false"}
                          onChange={(e) => setPodForm((f) => ({ ...f, isAccepted: e.target.value === "true" }))}
                          disabled={podSubmitting}
                        >
                          <option value="true">sí</option>
                          <option value="false">no</option>
                        </select>
                      </label>
                      <label className="field-inline field-block">
                        <span>Tipo confirmación</span>
                        <select
                          value={podForm.confirmationType}
                          onChange={(e) => setPodForm((f) => ({ ...f, confirmationType: e.target.value as any }))}
                          disabled={podSubmitting}
                        >
                          <option value="verbal">verbal</option>
                          <option value="manual">manual</option>
                          <option value="system">system</option>
                        </select>
                      </label>
                      <label className="field-inline field-block">
                        <span>Resultado entrega (7.1)</span>
                        <select
                          value={podForm.deliveryOutcome}
                          onChange={(e) => setPodForm((f) => ({ ...f, deliveryOutcome: e.target.value as any }))}
                          disabled={podSubmitting}
                        >
                          <option value="">—</option>
                          <option value="accepted">accepted</option>
                          <option value="refused">refused</option>
                          <option value="partial">partial</option>
                          <option value="other">other</option>
                        </select>
                      </label>
                      <label className="field-inline field-block">
                        <span>Receptor</span>
                        <input
                          value={podForm.receiverName}
                          onChange={(e) => setPodForm((f) => ({ ...f, receiverName: e.target.value }))}
                          disabled={podSubmitting}
                        />
                      </label>
                      <label className="field-inline field-block">
                        <span>Documento receptor (7.1)</span>
                        <input
                          value={podForm.receiverDocumentId}
                          onChange={(e) => setPodForm((f) => ({ ...f, receiverDocumentId: e.target.value }))}
                          disabled={podSubmitting}
                        />
                      </label>
                      <label className="field-inline field-block">
                        <span>Rol receptor (7.1)</span>
                        <input
                          value={podForm.receiverRole}
                          onChange={(e) => setPodForm((f) => ({ ...f, receiverRole: e.target.value }))}
                          disabled={podSubmitting}
                        />
                      </label>
                      <label className="field-inline field-block">
                        <span>Referencia entrega</span>
                        <input
                          value={podForm.deliveryReference}
                          onChange={(e) => setPodForm((f) => ({ ...f, deliveryReference: e.target.value }))}
                          disabled={podSubmitting}
                        />
                      </label>
                      <label className="field-inline field-block">
                        <span>Motivo aceptación (7.1)</span>
                        <input
                          value={podForm.acceptanceReason}
                          onChange={(e) => setPodForm((f) => ({ ...f, acceptanceReason: e.target.value }))}
                          disabled={podSubmitting}
                        />
                      </label>
                      <label className="field-inline field-block">
                        <span>Evidencia (texto) (7.1)</span>
                        <input
                          value={podForm.evidenceText}
                          onChange={(e) => setPodForm((f) => ({ ...f, evidenceText: e.target.value }))}
                          disabled={podSubmitting}
                        />
                      </label>
                      <div className="field-inline field-block">
                        <span>Foto evidencia (7.5) — subir archivo</span>
                        <div className="media-upload-row">
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,application/pdf"
                            disabled={photoUploading || podSubmitting}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) void handlePhotoUpload(f);
                            }}
                          />
                          {photoUploading ? <span className="text-muted small">Subiendo…</span> : null}
                          {photoUploadError ? <span className="form-error small">{photoUploadError}</span> : null}
                          {podForm.evidencePhotoUrl ? (
                            <a href={podForm.evidencePhotoUrl} target="_blank" rel="noreferrer" className="text-muted small">
                              Ver foto ↗
                            </a>
                          ) : null}
                        </div>
                        <input
                          value={podForm.evidencePhotoUrl}
                          onChange={(e) => setPodForm((f) => ({ ...f, evidencePhotoUrl: e.target.value }))}
                          disabled={podSubmitting}
                          placeholder="o pega URL directamente…"
                          className="mt-1"
                        />
                      </div>
                      <div className="field-inline field-block">
                        <span>Firma (7.5) — subir archivo</span>
                        <div className="media-upload-row">
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            disabled={sigUploading || podSubmitting}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) void handleSigUpload(f);
                            }}
                          />
                          {sigUploading ? <span className="text-muted small">Subiendo…</span> : null}
                          {podForm.evidenceSignatureUrl ? (
                            <a href={podForm.evidenceSignatureUrl} target="_blank" rel="noreferrer" className="text-muted small">
                              Ver firma ↗
                            </a>
                          ) : null}
                        </div>
                        <input
                          value={podForm.evidenceSignatureUrl}
                          onChange={(e) => setPodForm((f) => ({ ...f, evidenceSignatureUrl: e.target.value }))}
                          disabled={podSubmitting}
                          placeholder="o pega URL directamente…"
                          className="mt-1"
                        />
                      </div>
                      <label className="field-inline field-block">
                        <span>Lat (7.5)</span>
                        <input
                          value={podForm.capturedLat}
                          onChange={(e) => setPodForm((f) => ({ ...f, capturedLat: e.target.value }))}
                          disabled={podSubmitting}
                          placeholder="40.4168"
                        />
                      </label>
                      <label className="field-inline field-block">
                        <span>Lng (7.5)</span>
                        <input
                          value={podForm.capturedLng}
                          onChange={(e) => setPodForm((f) => ({ ...f, capturedLng: e.target.value }))}
                          disabled={podSubmitting}
                          placeholder="-3.7038"
                        />
                      </label>
                      <label className="field-inline field-block">
                        <span>Precisión m (7.5)</span>
                        <input
                          value={podForm.capturedAccuracyM}
                          onChange={(e) => setPodForm((f) => ({ ...f, capturedAccuracyM: e.target.value }))}
                          disabled={podSubmitting}
                          placeholder="12.3"
                        />
                      </label>
                      <label className="field-inline field-block">
                        <span>Proveedor ubicación (7.5)</span>
                        <input
                          value={podForm.capturedLocationProvider}
                          onChange={(e) => setPodForm((f) => ({ ...f, capturedLocationProvider: e.target.value }))}
                          disabled={podSubmitting}
                          placeholder="gps|network|manual"
                        />
                      </label>
                      <label className="field-inline field-block">
                        <span>Fecha ubicación (ISO) (7.5)</span>
                        <input
                          value={podForm.capturedLocationAt}
                          onChange={(e) => setPodForm((f) => ({ ...f, capturedLocationAt: e.target.value }))}
                          disabled={podSubmitting}
                          placeholder="2026-03-31T10:00:00+01:00"
                        />
                      </label>
                      <label className="field-inline field-block">
                        <span>Notas captura (7.1)</span>
                        <input
                          value={podForm.captureNotes}
                          onChange={(e) => setPodForm((f) => ({ ...f, captureNotes: e.target.value }))}
                          disabled={podSubmitting}
                        />
                      </label>
                      <label className="field-inline field-block">
                        <span>Notas</span>
                        <input
                          value={podForm.notes}
                          onChange={(e) => setPodForm((f) => ({ ...f, notes: e.target.value }))}
                          disabled={podSubmitting}
                        />
                      </label>
                      <label className="field-inline field-block">
                        <span>Motivo sustitución (7.7, opcional)</span>
                        <textarea
                          value={podForm.substitutionReason}
                          onChange={(e) => setPodForm((f) => ({ ...f, substitutionReason: e.target.value }))}
                          disabled={podSubmitting}
                          rows={2}
                          placeholder="Si sustituye un POD anterior, indique el motivo"
                        />
                      </label>
                    </div>
                    <div className="status-actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={podSubmitting || detail.order.status === "draft"}
                        onClick={() => void submitPod()}
                      >
                        {podSubmitting ? "Registrando…" : "Registrar POD"}
                      </button>
                    </div>
                  </section>
                ) : null}
                {detail.order.hasPod === true ? (
                  <section className="card">
                    <h3 className="subsection-title">POD (detalle)</h3>
                    {podError ? <p className="form-error">{podError}</p> : null}
                    {podLoading ? (
                      <p className="text-muted">Cargando POD…</p>
                    ) : pod ? (
                      <dl className="order-meta">
                        <dt>Intento vigente</dt>
                        <dd>
                          <code>#{pod.attemptNumber}</code>
                        </dd>
                        <dt>Registrado</dt>
                        <dd className="nowrap">{new Date(pod.recordedAt).toLocaleString()}</dd>
                        <dt>Aceptado</dt>
                        <dd>{pod.isAccepted ? "sí" : "no"}</dd>
                        <dt>Tipo confirmación</dt>
                        <dd>
                          <code>{pod.confirmationType}</code>
                        </dd>
                        <dt>Resultado entrega</dt>
                        <dd>{pod.deliveryOutcome ?? "—"}</dd>
                        <dt>Receptor</dt>
                        <dd>{pod.receiverName ?? "—"}</dd>
                        <dt>Documento receptor</dt>
                        <dd>{pod.receiverDocumentId ?? "—"}</dd>
                        <dt>Rol receptor</dt>
                        <dd>{pod.receiverRole ?? "—"}</dd>
                        <dt>Motivo aceptación</dt>
                        <dd>{pod.acceptanceReason ?? "—"}</dd>
                        <dt>Evidencia (texto)</dt>
                        <dd>{pod.evidenceText ?? "—"}</dd>
                        <dt>URL foto</dt>
                        <dd>{pod.evidencePhotoUrl ?? "—"}</dd>
                        <dt>URL firma</dt>
                        <dd>{pod.evidenceSignatureUrl ?? "—"}</dd>
                        <dt>Lat</dt>
                        <dd>{pod.capturedLat ?? "—"}</dd>
                        <dt>Lng</dt>
                        <dd>{pod.capturedLng ?? "—"}</dd>
                        <dt>Precisión m</dt>
                        <dd>{pod.capturedAccuracyM ?? "—"}</dd>
                        <dt>Proveedor ubicación</dt>
                        <dd>{pod.capturedLocationProvider ?? "—"}</dd>
                        <dt>Fecha ubicación</dt>
                        <dd>{pod.capturedLocationAt ?? "—"}</dd>
                        <dt>Canal captura</dt>
                        <dd>{pod.capturedByChannel ?? "—"}</dd>
                        <dt>Notas captura</dt>
                        <dd>{pod.captureNotes ?? "—"}</dd>
                        <dt>Referencia entrega</dt>
                        <dd>{pod.deliveryReference ?? "—"}</dd>
                        <dt>Notas</dt>
                        <dd>{pod.notes ?? "—"}</dd>
                        <dt>Motivo sustitución</dt>
                        <dd>{pod.substitutionReason ?? "—"}</dd>
                      </dl>
                    ) : (
                      <p className="text-muted small">No hay POD (o no se pudo cargar).</p>
                    )}
                    {podAttempts && podAttempts.length > 1 ? (
                      <div className="pod-attempts-hint">
                        <h4 className="subsection-title">Historial de intentos ({podAttempts.length})</h4>
                        <ul className="text-muted small">
                          {podAttempts.map((a) => (
                            <li key={a.id}>
                              #{a.attemptNumber} · {new Date(a.recordedAt).toLocaleString()} ·{" "}
                              {a.isAccepted ? "aceptado" : "no aceptado"} · {a.confirmationType}
                              {a.substitutionReason ? ` · ${a.substitutionReason}` : ""}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </section>
                ) : null}
                {nextStatusOptions.length > 0 ? (
                  <div className="status-actions">
                    <span className="text-muted small">Avanzar estado:</span>
                    {nextStatusOptions.map((st) => (
                      <button
                        key={st}
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={statusPatching}
                        onClick={() => void applyStatusPatch(st)}
                      >
                        → {st}
                      </button>
                    ))}
                  </div>
                ) : null}

                {/* ── Operativa: picking, pack, ship, deliver ── */}
                {(detail.order.status === "confirmed" || detail.order.status === "picking") ? (
                  <section className="card">
                    <h3 className="subsection-title">Picking</h3>
                    {packError ? <p className="form-error">{packError}</p> : null}
                    {pickLine ? (
                      <div className="filters-row">
                        <span className="text-muted small">Línea: <code>{pickLine.lineLabel}</code></span>
                        <label className="field-inline">
                          <span>Cantidad</span>
                          <input
                            type="number"
                            min={0.0001}
                            step="any"
                            value={pickQty}
                            onChange={(e) => setPickQty(Number(e.target.value))}
                            disabled={pickLoading}
                          />
                        </label>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => void submitPick()} disabled={pickLoading}>
                          {pickLoading ? "Registrando…" : "Confirmar picking"}
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPickLine(null)} disabled={pickLoading}>
                          Cancelar
                        </button>
                        {pickError ? <p className="form-error">{pickError}</p> : null}
                      </div>
                    ) : (
                      <p className="text-muted small">Seleccione una línea en la tabla para registrar picking.</p>
                    )}
                    <div className="table-wrap">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>SKU</th>
                            <th>Producto</th>
                            <th className="num">Pedido</th>
                            <th className="num">Picado</th>
                            <th className="num">Pendiente</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.lines.map((l) => (
                            <tr key={l.id} className={pickLine?.lineId === l.id ? "row-selected" : undefined}>
                              <td><code>{l.skuSnapshot ?? "—"}</code></td>
                              <td>{l.productNameSnapshot ?? "—"}</td>
                              <td className="num">{l.quantity}</td>
                              <td className="num">{l.pickedQuantity ?? "0"}</td>
                              <td className="num">{l.remainingQuantity ?? l.quantity}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => {
                                    setPickLine({ lineId: l.id, lineLabel: l.skuSnapshot ?? l.id });
                                    setPickQty(Number(l.remainingQuantity ?? l.quantity));
                                    setPickError(null);
                                  }}
                                >
                                  Picar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="status-actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={packLoading}
                        onClick={() => void submitPack()}
                      >
                        {packLoading ? "Empaquetando…" : "Empaquetar (pack)"}
                      </button>
                    </div>
                  </section>
                ) : null}

                {detail.order.status === "packed" ? (
                  <section className="card">
                    <h3 className="subsection-title">Expedir (ship)</h3>
                    {!showShipForm ? (
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowShipForm(true)}>
                        Abrir formulario de expedición
                      </button>
                    ) : (
                      <>
                        {shipError ? <p className="form-error">{shipError}</p> : null}
                        <div className="form-grid">
                          <label className="field-inline field-block">
                            <span>Transportista</span>
                            <input value={shipForm.carrierName} onChange={(e) => setShipForm((f) => ({ ...f, carrierName: e.target.value }))} disabled={shipLoading} />
                          </label>
                          <label className="field-inline field-block">
                            <span>Código transportista</span>
                            <input value={shipForm.carrierCode} onChange={(e) => setShipForm((f) => ({ ...f, carrierCode: e.target.value }))} disabled={shipLoading} />
                          </label>
                          <label className="field-inline field-block">
                            <span>Nivel de servicio</span>
                            <input value={shipForm.serviceLevel} onChange={(e) => setShipForm((f) => ({ ...f, serviceLevel: e.target.value }))} disabled={shipLoading} placeholder="express, standard…" />
                          </label>
                          <label className="field-inline field-block">
                            <span>Ref. tracking</span>
                            <input value={shipForm.trackingReference} onChange={(e) => setShipForm((f) => ({ ...f, trackingReference: e.target.value }))} disabled={shipLoading} />
                          </label>
                          <label className="field-inline field-block">
                            <span>Ref. despacho</span>
                            <input value={shipForm.dispatchReference} onChange={(e) => setShipForm((f) => ({ ...f, dispatchReference: e.target.value }))} disabled={shipLoading} />
                          </label>
                          <label className="field-inline field-block">
                            <span>Ref. externa</span>
                            <input value={shipForm.externalReference} onChange={(e) => setShipForm((f) => ({ ...f, externalReference: e.target.value }))} disabled={shipLoading} />
                          </label>
                          <label className="field-inline field-block">
                            <span>Fecha expedición (ISO, opcional)</span>
                            <input value={shipForm.shippedAt} onChange={(e) => setShipForm((f) => ({ ...f, shippedAt: e.target.value }))} disabled={shipLoading} placeholder="2026-05-05T10:00:00+02:00" />
                          </label>
                          <label className="field-inline field-block">
                            <span>Notas envío</span>
                            <input value={shipForm.shippingNotes} onChange={(e) => setShipForm((f) => ({ ...f, shippingNotes: e.target.value }))} disabled={shipLoading} />
                          </label>
                        </div>
                        <div className="status-actions">
                          <button type="button" className="btn btn-primary btn-sm" onClick={() => void submitShip()} disabled={shipLoading}>
                            {shipLoading ? "Expidiendo…" : "Confirmar expedición"}
                          </button>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowShipForm(false)} disabled={shipLoading}>
                            Cancelar
                          </button>
                        </div>
                      </>
                    )}
                  </section>
                ) : null}

                {detail.order.status === "shipped" ? (
                  <section className="card">
                    <h3 className="subsection-title">Registrar entrega (deliver)</h3>
                    {!showDeliverForm ? (
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowDeliverForm(true)}>
                        Abrir formulario de entrega
                      </button>
                    ) : (
                      <>
                        {deliverError ? <p className="form-error">{deliverError}</p> : null}
                        <div className="form-grid">
                          <label className="field-inline field-block">
                            <span>Receptor</span>
                            <input value={deliverForm.receiverName} onChange={(e) => setDeliverForm((f) => ({ ...f, receiverName: e.target.value }))} disabled={deliverLoading} />
                          </label>
                          <label className="field-inline field-block">
                            <span>Ref. entrega</span>
                            <input value={deliverForm.deliveryReference} onChange={(e) => setDeliverForm((f) => ({ ...f, deliveryReference: e.target.value }))} disabled={deliverLoading} />
                          </label>
                          <label className="field-inline field-block">
                            <span>Fecha entrega (ISO, opcional)</span>
                            <input value={deliverForm.deliveredAt} onChange={(e) => setDeliverForm((f) => ({ ...f, deliveredAt: e.target.value }))} disabled={deliverLoading} placeholder="2026-05-05T14:30:00+02:00" />
                          </label>
                          <label className="field-inline field-block">
                            <span>Notas</span>
                            <input value={deliverForm.deliveryNotes} onChange={(e) => setDeliverForm((f) => ({ ...f, deliveryNotes: e.target.value }))} disabled={deliverLoading} />
                          </label>
                          <label className="field-inline field-block">
                            <span>Motivo estado (opcional)</span>
                            <input value={deliverForm.statusReason} onChange={(e) => setDeliverForm((f) => ({ ...f, statusReason: e.target.value }))} disabled={deliverLoading} placeholder="Bloqueo, incidencia…" />
                          </label>
                        </div>
                        <div className="status-actions">
                          <button type="button" className="btn btn-primary btn-sm" onClick={() => void submitDeliver()} disabled={deliverLoading}>
                            {deliverLoading ? "Registrando…" : "Confirmar entrega"}
                          </button>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowDeliverForm(false)} disabled={deliverLoading}>
                            Cancelar
                          </button>
                        </div>
                      </>
                    )}
                  </section>
                ) : null}
                {(detail.reservations ?? []).length > 0 ? (
                  <>
                    <h3 className="subsection-title">Reservas de inventario</h3>
                    <div className="table-wrap">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Estado</th>
                            <th className="num">Cantidad</th>
                            <th>Producto</th>
                            <th>Almacén</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.reservations ?? []).map((r) => (
                            <tr key={r.id}>
                              <td>
                                <code>{r.status}</code>
                              </td>
                              <td className="num">{r.reservedQuantity}</td>
                              <td>
                                <code>{r.productId.slice(0, 8)}…</code>
                              </td>
                              <td>
                                <code>{r.warehouseId.slice(0, 8)}…</code>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : null}
                <h3 className="subsection-title">Líneas</h3>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Producto</th>
                        <th className="num">Cantidad</th>
                        <th>Unidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.lines.map((l) => (
                        <tr key={l.id}>
                          <td>
                            <code>{l.skuSnapshot ?? "—"}</code>
                          </td>
                          <td>{l.productNameSnapshot ?? "—"}</td>
                          <td className="num">{l.quantity}</td>
                          <td>{l.unit ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="form-error">Sin datos de detalle.</p>
            )}
          </section>
        </div>
      ) : null}

      <section className="card filters-card create-order-card">
        <h2 className="section-title">Nuevo pedido (borrador)</h2>
        <p className="text-muted small">
          Se crea en estado <code>draft</code>. Use el detalle para <code>submitted</code> → <code>confirmed</code> (reserva
          stock) o cancelar.
        </p>
        {needsTenantPicker ? (
          <label className="field-inline field-block">
            <span>Tenant</span>
            <select value={formTenantId} onChange={(e) => setFormTenantId(e.target.value)}>
              <option value="">— Elegir —</option>
              {(tenants ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.slug} — {t.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <div className="filters-row">
          <label className="field-inline">
            <span>Número (opcional)</span>
            <input value={formOrderNumber} onChange={(e) => setFormOrderNumber(e.target.value)} placeholder="Auto si vacío" />
          </label>
          <label className="field-inline">
            <span>Ref. cliente</span>
            <input value={formCustomerRef} onChange={(e) => setFormCustomerRef(e.target.value)} placeholder="PO-…" />
          </label>
        </div>
        <h3 className="subsection-title">Líneas</h3>
        {formLines.map((line, i) => (
          <div key={i} className="form-line-row filters-row">
            <label className="field-inline field-grow">
              <span>Producto</span>
              <select value={line.productId} onChange={(e) => updateFormLine(i, { productId: e.target.value })}>
                <option value="">— Elegir —</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.sku} — {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-inline">
              <span>Cantidad</span>
              <input
                type="number"
                min={0.0001}
                step="any"
                value={line.quantity}
                onChange={(e) => updateFormLine(i, { quantity: Number(e.target.value) })}
              />
            </label>
            <button type="button" className="btn btn-secondary" onClick={() => removeFormLine(i)} disabled={formLines.length <= 1}>
              Quitar
            </button>
          </div>
        ))}
        <div className="filters-row">
          <button type="button" className="btn btn-secondary" onClick={addFormLine}>
            + Línea
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void submitCreate()} disabled={createSubmitting}>
            {createSubmitting ? "Creando…" : "Crear pedido"}
          </button>
        </div>
        {createMsg ? <p className={createMsg.includes("correctamente") ? "text-muted" : "form-error"}>{createMsg}</p> : null}
      </section>

      <section className="card gaps-card">
        {/* Documentación oficial: https://github.com/jowy81/0xvera-trace-docs/blob/main/docs/00-overview/01-product.md#pedidos */}
        <h2 className="section-title">Alcance y próximas fases</h2>
        <p className="text-muted small">
          <a
            href="https://github.com/jowy81/0xvera-trace-docs/blob/main/docs/00-overview/01-product.md#pedidos"
            target="_blank"
            rel="noreferrer"
          >
            Documentación oficial (pedidos)
          </a>
        </p>
      </section>
    </div>
  );
}
